import { useState, useEffect, useCallback, useRef } from 'react';
import { metarFogForecastService } from '../../services/MetarFogForecastService';
import { mapForecastToTimelineData, mapForecastToHourlyTimelineData, getAirportName } from '../../utils/SharedForecastTimelineMapper';
import { ForecastTimelineData, ErrorInfo } from '../../models/ForecastTimelineData';
import { HourlyForecastTimelineData } from '../../models/HourlyForecastTimelineData';
import { useDataRefresh } from '../useDataRefresh';

// Extended timeline data interface (same as MapPage)
interface ExtendedTimelineData extends ForecastTimelineData {
  errors?: ErrorInfo[];
}

// Extended hourly timeline data for METAR adapter
interface ExtendedHourlyTimelineData extends HourlyForecastTimelineData {
  errors?: ErrorInfo[];
  operatingHours?: { startHour: number; endHour: number; operatingHoursString: string };
}

interface UseTimelineDataResult {
  timelineData: Record<number, ExtendedTimelineData>;
  hourlyTimelineData: Record<number, ExtendedHourlyTimelineData>;
  loading: boolean;
  error: string | null;
  hasDataError: boolean;
  refresh: () => Promise<void>;
  lastUpdated: Date;
  refreshCount: number;
}

/**
 * Custom hook to fetch and manage timeline data for multiple locations
 * Consolidates the timeline fetching logic from MapPage and TimelinePage
 *
 * @param locationIds - Array of location IDs to fetch forecasts for
 * @param customNow - Optional custom "now" time for timeline generation
 * @param refreshInterval - Interval in minutes for auto-refresh (default: 5)
 * @returns Timeline data, loading state, error state, and refresh function
 */
export function useSharedTimelineData(
  locationIds: number[],
  customNow?: Date | null,
  refreshInterval: number = 5
): UseTimelineDataResult {
  const [timelineData, setTimelineData] = useState<Record<number, ExtendedTimelineData>>({});
  const [hourlyTimelineData, setHourlyTimelineData] = useState<Record<number, ExtendedHourlyTimelineData>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasDataError, setHasDataError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [refreshCount, setRefreshCount] = useState(0);
  const isMounted = useRef(true);
  const firstChunkReceived = useRef(false);
  const lastRefreshTime = useRef<number>(0);
  const boundaryIntervalRef = useRef<number | null>(null);

  // Fetch forecast data for all locations
  const fetchForecastData = useCallback(async (isRefresh: boolean = false) => {
    // Skip fetching if no locations - only update state if needed to avoid re-renders
    if (locationIds.length === 0) {
      if (!isRefresh) {
        setLoading(false);
      }
      return;
    }

    // Only show loading and clear data on initial load, not on refresh
    if (!isRefresh) {
      setLoading(true);
      setTimelineData({});
      setHourlyTimelineData({});
      firstChunkReceived.current = false; // Reset for new fetch
    }
    setError(null);
    setHasDataError(false);

    try {

      // Fetch METAR forecasts with progressive updates
      const result = await metarFogForecastService.getBatchMetarFogForecast(
        locationIds,
        customNow || undefined,
        // Progressive callback - update UI as each chunk arrives
        (chunkForecasts) => {
          // Process this chunk and update state progressively
          const chunkData: Record<number, ExtendedTimelineData> = {};
          const chunkHourlyData: Record<number, ExtendedHourlyTimelineData> = {};

          chunkForecasts.forEach((forecast, index) => {
            if (!forecast) {
              console.error(`No forecast data for location at chunk index ${index}`);
              return;
            }

            // Extract location ID from the forecast data itself
            const locationId =
              forecast.aggregated_predictions_30min?.[0]?.location ??
              forecast.aggregated_predictions_hourly?.[0]?.location ??
              forecast.aggregated_actuals?.[0]?.location;

            if (!locationId) {
              console.error(`Cannot determine location ID from forecast at chunk index ${index}`);
              return;
            }

            // Validate this location was actually requested
            if (!locationIds.includes(locationId)) {
              return;
            }

            const airportName = getAirportName(locationId);

            // Map forecast to 30-min timeline data
            const mappedData = mapForecastToTimelineData(
              forecast,
              locationId,
              airportName
            ) as ExtendedTimelineData;

            chunkData[locationId] = mappedData;

            // Also map to hourly timeline data (60-min predictions)
            const mappedHourlyData = mapForecastToHourlyTimelineData(
              forecast,
              locationId,
              airportName
            ) as ExtendedHourlyTimelineData;

            chunkHourlyData[locationId] = mappedHourlyData;
          });

          // Only update state if component is still mounted
          if (!isMounted.current) return;

          // Merge chunk data with existing data
          setTimelineData(prev => ({
            ...prev,
            ...chunkData
          }));

          setHourlyTimelineData(prev => ({
            ...prev,
            ...chunkHourlyData
          }));

          // Hide loading spinner after first chunk (only on initial load)
          if (!firstChunkReceived.current && !isRefresh) {
            setLoading(false);
            firstChunkReceived.current = true;
          }
        }
      );

      // Only update state if component is still mounted
      if (!isMounted.current) return;

      setLastUpdated(new Date());
      lastRefreshTime.current = Date.now();

      // Set data error state if we had partial failures
      if (result.hasPartialFailure) {
        setHasDataError(true);
      }

      // Check if we got any data (check after all chunks complete)
      setTimelineData(current => {
        if (Object.keys(current).length === 0) {
          setError('No forecast data available');
        }
        return current;
      });
    } catch (err) {
      console.error('Error fetching forecast data:', err);

      // Only update state if component is still mounted
      if (!isMounted.current) return;

      setError('Failed to load forecast data');
      if (!isRefresh) {
        setLoading(false);
      }
    }
  }, [locationIds, customNow]);

  // Wrapper for auto-refresh (marks as refresh to avoid clearing data)
  const autoRefresh = useCallback(async () => {
    console.log(`[SharedTimelineData] Auto refresh at ${new Date().toISOString()}`);
    lastRefreshTime.current = Date.now();
    setRefreshCount(prev => prev + 1);
    await fetchForecastData(true);
  }, [fetchForecastData]);

  // Wrapper for manual refresh (also marks as refresh)
  const manualRefresh = useCallback(async () => {
    console.log(`[SharedTimelineData] Manual refresh at ${new Date().toISOString()}`);
    lastRefreshTime.current = Date.now();
    setRefreshCount(prev => prev + 1);
    await fetchForecastData(true);
  }, [fetchForecastData]);

  // Use data refresh hook for aligned refreshing
  // Pass customNow to disable auto-refresh when using custom time
  useDataRefresh(autoRefresh, refreshInterval, customNow);

  // Segment boundary refresh - triggers refresh at 15-minute boundaries (:00, :15, :30, :45)
  // This ensures data is fresh when timeline segments tick over
  useEffect(() => {
    // Skip if using custom time
    if (customNow) return;

    const boundaryInterval = 15; // Refresh every 15 minutes
    const minTimeBetweenRefreshes = 30000; // Skip if refresh happened within 30 seconds

    // Only refresh if enough time has passed since last refresh
    const boundaryRefresh = () => {
      const timeSinceLastRefresh = Date.now() - lastRefreshTime.current;
      if (timeSinceLastRefresh >= minTimeBetweenRefreshes) {
        console.log(`[SharedTimelineData] Boundary refresh at ${new Date().toISOString()}`);
        autoRefresh();
      } else {
        console.log(`[SharedTimelineData] Boundary refresh skipped - last refresh was ${Math.round(timeSinceLastRefresh / 1000)}s ago`);
      }
    };

    const setupBoundaryRefresh = () => {
      const now = new Date();
      const minutes = now.getMinutes();
      const seconds = now.getSeconds();
      const milliseconds = now.getMilliseconds();

      // Calculate next 15-minute boundary (0, 15, 30, or 45)
      const currentQuarter = Math.floor(minutes / 15);
      const nextBoundary = (currentQuarter + 1) * 15;
      const minutesToNext = nextBoundary - minutes;

      // Calculate milliseconds until next boundary
      const msUntilBoundary = (minutesToNext * 60 * 1000) - (seconds * 1000) - milliseconds;

      return window.setTimeout(() => {
        boundaryRefresh();
        // Set up next boundary refresh
        const intervalId = window.setInterval(() => {
          boundaryRefresh();
        }, boundaryInterval * 60 * 1000);

        // Store interval ID for cleanup
        boundaryIntervalRef.current = intervalId;
      }, msUntilBoundary);
    };

    const timeoutId = setupBoundaryRefresh();

    return () => {
      clearTimeout(timeoutId);
      if (boundaryIntervalRef.current) {
        clearInterval(boundaryIntervalRef.current);
      }
    };
  }, [autoRefresh, customNow]);

  // Initial data fetch and refetch when locationIds or customNow changes
  useEffect(() => {
    console.log(`[SharedTimelineData] Initial fetch at ${new Date().toISOString()}`);
    fetchForecastData(false);
  }, [fetchForecastData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return {
    timelineData,
    hourlyTimelineData,
    loading,
    error,
    hasDataError,
    refresh: manualRefresh,
    lastUpdated,
    refreshCount
  };
}